(ns admin.events.learning-objectives
  (:require [kyber.events.utils :refer [add-auth base-uri kyber-event-fx]]
            [kyber.lib.kyber-db :as kyber-db]
            [kyber.events.eql :as eql]
            [kyber.utils :as ku]))

(defn- assoc-ordinal
  [[lo-uuid lo] idx]
  [lo-uuid (assoc lo :ordinal (inc idx))])

(defn- add-updated-ordinals
  [los]
  (into {}
        (map-indexed (fn [idx item]
                       (assoc-ordinal item idx))
                     los)))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; get-learning-objectives
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-get-learning-objectives-success
    {:description "Get learning-objectives success"
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn get-learning-objectives-success
  [{:keys [db]} [event-name response]]
  {:db (assoc db
              :learning-objectives
              (->> response
                   (map #(assoc % :is-editing false))
                   (map #(assoc % :disabled false))
                   (map (juxt :uuid identity))
                   (into {})
                   add-updated-ordinals))
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx :learning-objectives/get-learning-objectives-success
                get-learning-objectives-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-get-learning-objectives-failure
    {:description "Get learning-objectives failure"
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn get-learning-objectives-failure
  [{:keys [db]} [event-name _err]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx :learning-objectives/get-learning-objectives-failure
                get-learning-objectives-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-get-learning-objectives
    {:description "Get learning-objectives request"
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn get-learning-objectives
  [{:keys [db]} [event-name {:keys [curriculum-uuid activity-uuid]}]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]
   :request
   {:name event-name
    :method :get
    :uri (base-uri db (str "/v1/admin/curricula/" curriculum-uuid
                           "/activities/" activity-uuid
                           "/learning-objectives"))
    :response-format :json
    :headers (add-auth db)
    :on-success [:learning-objectives/get-learning-objectives-success]
    :on-failure [:learning-objectives/get-learning-objectives-failure]}})

(kyber-event-fx :learning-objectives/get-learning-objectives
                get-learning-objectives)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; create-learning-objective
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-create-learning-objective-success
    {:description ""
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn create-learning-objective-success
  [{:keys [db]} [event-name
                 on-success
                 {:keys [activity-uuid curriculum-uuid]}
                 response]]
  {:db (kyber-db/tx-record db
                           {:model :activity}
                           {:activity/uuid activity-uuid
                            :activity/learning-objectives
                            [(ku/map->nsmap :learning-objective response)]})
   :dispatch-n [[:activities/get-activity-overview
                 {:activity-uuid activity-uuid
                  :curriculum-uuid curriculum-uuid}]
                [:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]
   :invoke on-success})

(kyber-event-fx :learning-objectives/create-learning-objective-success
                create-learning-objective-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-create-learning-objective-failure
    {:description ""
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn create-learning-objective-failure
  [{:keys [db]} [event-name _err]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx :learning-objectives/create-learning-objective-failure
                create-learning-objective-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-create-learning-objective
    {:description ""
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn create-learning-objective
  [{:keys [db]} [event-name learning-objective on-success]]
  (let [{:keys [curriculum-uuid activity-uuid]} learning-objective]
    {:db db
     :dispatch-n [[:analytics/save-datapoint {:event event-name
                                              :event-version 0}]]
     :request
     {:name event-name
      :method :post
      :uri (base-uri db (str "/v1/admin/curricula/" curriculum-uuid
                             "/activities/" activity-uuid
                             "/learning-objectives"))
      :params (dissoc learning-objective
                      :curriculum-uuid
                      :activity-uuid)
      :format :json
      :response-format :json
      :headers (add-auth db)
      :on-success [:learning-objectives/create-learning-objective-success
                   on-success
                   {:curriculum-uuid curriculum-uuid
                    :activity-uuid activity-uuid}]
      :on-failure [:learning-objectives/create-learning-objective-failure]}}))

(kyber-event-fx :learning-objectives/create-learning-objective
                create-learning-objective)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; delete-learning-objective
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-delete-learning-objective-success
    {:description ""
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn delete-learning-objective-success
  [{:keys [db]} [event-name on-success {:keys [learning-objective-uuid
                                               curriculum-uuid
                                               activity-uuid]}]]
  {:db (-> db
           (kyber-db/retract-ref
            {:model :activity
             :refs-key :activity/learning-objectives
             :ref-ident-key learning-objective-uuid}
            {:activity/uuid activity-uuid})
           (kyber-db/retract-record
            {:model :learning-objective}
            {:learning-objective/uuid learning-objective-uuid}))
   :dispatch-n [[:dispatch-in 500
                 :activities/get-activity-overview
                 {:activity-uuid activity-uuid
                  :curriculum-uuid curriculum-uuid}]
                [:learning-objectives/adjust-lo-sort-order
                 {:start-idx 0
                  :end-idx 0
                  :activity-uuid activity-uuid
                  :curriculum-uuid curriculum-uuid}]
                [:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]
   :invoke on-success})

(kyber-event-fx :learning-objectives/delete-learning-objective-success
                delete-learning-objective-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-delete-learning-objective-failure
    {:description ""
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn delete-learning-objective-failure
  [{:keys [db]} [event-name _err]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx :learning-objectives/delete-learning-objective-failure
                delete-learning-objective-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-delete-learning-objective
    {:description ""
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn delete-learning-objective
  [{:keys [db]}
   [event-name {:keys [learning-objective curriculum-uuid activity-uuid]} on-success]]

  (let [activity
        (kyber-db/get-record db :activity activity-uuid)

        learning-objective-uuid
        (:learning-objective/uuid learning-objective)

        attached-question-count
        (->> (concat (get-in activity [:activity/assessment :assessment/questions])
                     (get-in activity [:activity/evaluation :questionnaire/questions]))
             (filter #(= learning-objective-uuid
                         (get-in % [:question.context/learning-objective
                                    :learning-objective/uuid])))
             count)

        attached-resource-count
        (->> activity
             :activity/resources
             (filter #(= learning-objective-uuid
                         (get-in % [:resource/learning-objective
                                    :learning-objective/uuid])))
             count)

        attached-moment-count
        (->> activity
             :activity/moments
             (filter #(= learning-objective-uuid
                         (get-in % [:moment/learning-objective
                                    :learning-objective/uuid])))
             count)

        is-safe-to-remove
        (zero? (+ attached-resource-count
                  attached-moment-count
                  attached-question-count))

        moment-copy
        (str " moment" (when (> attached-moment-count 1) "s"))

        question-copy
        (str " question" (when (> attached-question-count 1) "s"))

        resource-copy
        (str " resource" (when (> attached-resource-count 1) "s"))

        message (cond
                  (and (pos? attached-resource-count)
                       (pos? attached-moment-count)
                       (pos? attached-question-count))
                  (str attached-resource-count resource-copy
                       ", "
                       attached-moment-count moment-copy
                       ", and "
                       attached-question-count question-copy)

                  (and (pos? attached-resource-count)
                       (pos? attached-moment-count))
                  (str attached-resource-count resource-copy
                       " and  "
                       attached-moment-count moment-copy)

                  (and (pos? attached-resource-count)
                       (pos? attached-question-count))
                  (str attached-resource-count resource-copy
                       " and "
                       attached-question-count question-copy)

                  (and (pos? attached-moment-count)
                       (pos? attached-question-count))
                  (str attached-moment-count moment-copy
                       " and "
                       attached-question-count question-copy)

                  (pos? attached-question-count)
                  (str attached-question-count question-copy)

                  (pos? attached-resource-count)
                  (str attached-resource-count resource-copy)

                  (pos? attached-moment-count)
                  (str attached-moment-count moment-copy))]

    (if (true? is-safe-to-remove)
      {:db db
       :dispatch-n [[:analytics/save-datapoint {:event event-name
                                                :event-version 0}]]
       :request
       {:name event-name
        :method :delete
        :uri (base-uri db (str "/v1/admin/curricula/" curriculum-uuid
                               "/activities/" activity-uuid
                               "/learning-objectives/" learning-objective-uuid))
        :format :json
        :response-format :json
        :headers (add-auth db)
        :on-success [:learning-objectives/delete-learning-objective-success
                     on-success
                     {:learning-objective-uuid learning-objective-uuid
                      :curriculum-uuid curriculum-uuid
                      :activity-uuid activity-uuid}]
        :on-failure [:learning-objectives/delete-learning-objective-failure]}}
      {:db db
       :dispatch-n [[:analytics/save-datapoint {:event event-name
                                                :event-version 0}]
                    [:notifier/create-alert
                     {:title "Learning Objective is already assigned"
                      :message
                      (str "Looks like this learning objective is assigned to "
                           message ". To remove this learning objective, "
                           "first remove it from all assigned resources, "
                           "moments, and questions.")
                      :confirm-only true}]]})))

(kyber-event-fx :learning-objectives/delete-learning-objective
                delete-learning-objective)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; update-learning-objective
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-update-learning-objective-success
    {:description ""
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn update-learning-objective-success
  [{:keys [db]} [event-name on-success _props response]]
  {:db (kyber-db/tx-record db
                           {:model :learning-objective}
                           (ku/map->nsmap :learning-objective response))
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]
   :invoke on-success})

(kyber-event-fx :learning-objectives/update-learning-objective-success
                update-learning-objective-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-update-learning-objective-failure
    {:description ""
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn update-learning-objective-failure
  [{:keys [db]} [event-name _err]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx :learning-objectives/update-learning-objective-failure
                update-learning-objective-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-update-learning-objective
    {:description ""
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn update-learning-objective
  [{:keys [db]} [event-name learning-objective on-success]]
  (let [{:keys [curriculum-uuid activity-uuid]} learning-objective]
    {:db db
     :dispatch-n [[:analytics/save-datapoint {:event event-name
                                              :event-version 0}]]
     :request
     {:name event-name
      :method :patch
      :uri (base-uri db (str "/v1/admin/curricula/" curriculum-uuid
                             "/activities/" activity-uuid
                             "/learning-objectives/"
                             (:uuid learning-objective)))
      :params (dissoc learning-objective
                      :uuid
                      :curriculum-uuid
                      :activity-uuid)
      :format :json
      :response-format :json
      :headers (add-auth db)
      :on-success [:learning-objectives/update-learning-objective-success
                   on-success
                   learning-objective]
      :on-failure [:learning-objectives/update-learning-objective-failure]}}))

(kyber-event-fx :learning-objectives/update-learning-objective
                update-learning-objective)

(defn edit-disabled-state
  [{:keys [db]} [_ disabled-state]]
  {:db (update db :learning-objectives
               (fn [_]
                 (into {}
                       (map
                        (fn [item]
                          [(:uuid item) (assoc item :disabled disabled-state)])
                        (vals (:learning-objectives db))))))})

(kyber-event-fx :learning-objectives/edit-disabled-state edit-disabled-state)

(defn update-learning-objectives-sort-order-success
  [{:keys [db]} [event-name {:keys [learning-objectives activity-uuid]}]]
  {:db (kyber-db/tx-record db
                           {:model :activity}
                           {:activity/uuid activity-uuid
                            :activity/learning-objectives learning-objectives})
   :dispatch-n [[:learning-objectives/edit-disabled-state false]
                [:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx
 :learning-objectives/update-learning-objectives-sort-order-success
 update-learning-objectives-sort-order-success)

(defn update-learning-objectives-sort-order-failure
  [{:keys [db]} [event-name _]]
  {:db db
   :dispatch-n [[:learning-objectives/edit-disabled-state false]
                [:analytics/save-datapoint {:event event-name
                                            :event-version 0}]
                [:notifier/create-toast
                 {:message "Learning Objectives failed to update!"}]]})

(kyber-event-fx
 :learning-objectives/update-learning-objectives-sort-order-failure
 update-learning-objectives-sort-order-failure)

;; Update Learning Objective Sort Order
(defn update-learning-objectives-sort-order
  [{:keys [db]} [event-name {:keys [learning-objectives
                                    activity-uuid
                                    curriculum-uuid] :as props}]]
  (cond-> {:db db
           :dispatch-n [[:analytics/save-datapoint {:event event-name
                                                    :event-version 0}]]}

    ;;  Prevents a request to update LO sort order when there are no LOs.
    (seq learning-objectives)
    (assoc :request
           {:name event-name
            :method :patch
            :headers (add-auth db)
            :uri (base-uri db (str "/v1/admin/curricula/" curriculum-uuid
                                   "/activities/" activity-uuid
                                   "/learning-objectives"))
            :params (map ku/nsmap->map learning-objectives)
            :format :json
            :response-format :json
            :on-success [:learning-objectives/update-learning-objectives-sort-order-success props]
            :on-failure [:learning-objectives/update-learning-objectives-sort-order-failure]})))

(kyber-event-fx :learning-objectives/update-learning-objectives-sort-order
                update-learning-objectives-sort-order)

(defn- update-los
  [{:keys [end-idx los lo-item]}]
  (let [remaining-items (vec (remove #(= (:learning-objective/uuid lo-item)
                                         (:learning-objective/uuid %)) los))]
    (when (some? lo-item)
      (as-> (subvec remaining-items 0 end-idx) %
        (conj % lo-item)
        (into % (subvec remaining-items end-idx))
        (map-indexed #(assoc %2 :learning-objective/ordinal (inc %1)) %)
        (vec %)))))

(defn adjust-lo-sort-order
  [{:keys [db]} [event-name {:keys [start-idx
                                    end-idx
                                    activity-uuid
                                    curriculum-uuid]}]]
  (let [current-los (as-> db $
                      (kyber-db/get-record $ :activity activity-uuid)
                      (:activity/learning-objectives $)
                      (sort-by :learning-objective/ordinal $))
        lo-item (->> current-los
                     (filter #(= (dec (get % :learning-objective/ordinal))
                                 start-idx))
                     first)
        updated-los (update-los {:lo-item lo-item
                                 :end-idx end-idx
                                 :los current-los})]
    {:db db
     :dispatch-n [[:learning-objectives/edit-disabled-state true]
                  [:learning-objectives/update-learning-objectives-sort-order
                   {:learning-objectives updated-los
                    :curriculum-uuid curriculum-uuid
                    :activity-uuid activity-uuid}]
                  [:analytics/save-datapoint {:event event-name
                                              :event-version 0}]]}))

(kyber-event-fx :learning-objectives/adjust-lo-sort-order adjust-lo-sort-order)

;;;;;;;;;;;;;;;;;;;;;
;;;;; V2 Events ;;;;;
;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-get-learning-objectives-v2-success
    {:description "Get learning objectives success"
     :changelog
     [{:date "27JAN21"
       :event-version 0
       :data {}}]}))

(defn get-learning-objectives-v2-success
  [{:keys [db]} [event-name _ {{:activity/keys [learning-objectives]
                                activity-uuid :activity/uuid} :>/activity}]]
  (let [tx {:activity/uuid activity-uuid
            :activity/learning-objectives learning-objectives}]
    {:db (kyber-db/tx-record db {:model :activity} tx)
     :dispatch-n [[:analytics/save-datapoint
                   {:event event-name
                    :event-version 0}]]}))

(kyber-event-fx ::get-learning-objectives-v2-success get-learning-objectives-v2-success)


(when ^boolean js/goog.DEBUG
  (def event-doc-get-learning-objectives-v2-failure
    {:description "Get learning objectives failure"
     :changelog
     [{:date "27JAN21"
       :event-version 0
       :data {}}]}))

(defn get-learning-objectives-v2-failure
  [{:keys [db]} [event-name _err]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]})

(kyber-event-fx ::get-learning-objectives-v2-failure get-learning-objectives-v2-failure)

(defn get-learning-objectives-v2
  [{:keys [db]} [event-name {:keys [activity-uuid curriculum-uuid] :as params}]]
  (let [lo-sub-query [:learning-objective/uuid
                      :learning-objective/description
                      :learning-objective/label
                      :learning-objective/ordinal]]
    {:db db
     :dispatch-n [[:analytics/save-datapoint
                   {:event event-name
                    :event-version 0}]]
     :request
     (eql/request db
                  {:name event-name
                   :params `[{(:>/activity {:curriculum/uuid ~curriculum-uuid
                                            :activity/uuid ~activity-uuid})
                              [:activity/uuid
                               {:activity/learning-objectives ~lo-sub-query}]}]
                   :on-success [::get-learning-objectives-v2-success params]
                   :on-failure [::get-learning-objectives-v2-failure params]})}))

(kyber-event-fx ::get-learning-objectives-v2 get-learning-objectives-v2)
